import React from 'react'

export function formatCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) return '-'
  return `${Number(value).toLocaleString('ko-KR')}원`
}

export function formatDate(value, withTime = false) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date)
}

const STATUS_LABELS = {
  PROFILE_DRAFT: '프로필 작성중',
  REVIEW_REQUESTED: '심사 요청',
  UNDER_REVIEW: '심사 중',
  APPROVED: '승인',
  REJECTED: '반려',
  SUSPENDED: '이용 정지',
  VERIFIED: '확인 완료',
  DISTRIBUTED: '디렉터 전달',
  RECEIVING_QUOTES: '견적 수신 중',
  ANALYZED: 'AI 분석 완료',
  SELECTED: '견적 선택',
  SUBMITTED: '제출',
  EXPIRED: '만료',
  PAID: '결제 완료',
  PARTIALLY_REFUNDED: '부분 환불',
  REFUNDED: '환불 완료',
  REQUESTED: '처리 요청',
  UNDER_REVIEW_SETTLEMENT: '검토 중',
  PAYABLE: '지급 예정',
  READY: '준비 완료',
  IN_PROGRESS: '진행 중',
  COMPLETED: '완료',
  FAILED: '실패',
  ACTIVE: '사용 중',
  INACTIVE: '사용 중지',
  CONFIRMED: '확정',
  PUBLISHED: '공개',
  HIDDEN: '블라인드',
  DELETED: '삭제됨',
  PURGED: '완전 삭제',
  REPORTED: '신고 접수',
  DRAFT: '작성 중',
  SCHEDULED: '예정',
  ANSWERED: '답변 완료',
  OPEN: '답변 대기',
  NORMAL: '정상',
  DISPUTE: '분쟁 검토',
  INVITED: '초대됨',
  DONE: '완료',
  CURRENT: '현재 단계',
  UPCOMING: '예정',
  QUOTE_SUBMITTED: '견적 제출',
  VIEWED: '열람',
  DECLINED: '참여 안 함',
  SENT: '전달 완료',
}

export function StatusBadge({ status, children }) {
  const tone = (() => {
    if (['APPROVED', 'PAID', 'VERIFIED', 'READY', 'PAYABLE', 'ACTIVE', 'CONFIRMED', 'PUBLISHED', 'ANSWERED', 'COMPLETED', 'DONE', 'NORMAL', 'QUOTE_SUBMITTED'].includes(status)) return 'success'
    if (['REJECTED', 'SUSPENDED', 'REFUNDED', 'HIDDEN', 'DELETED', 'PURGED', 'REPORTED', 'DISPUTE', 'FAILED', 'DECLINED'].includes(status)) return 'danger'
    if (['UNDER_REVIEW', 'REVIEW_REQUESTED', 'DISTRIBUTED', 'RECEIVING_QUOTES', 'ANALYZED', 'REQUESTED', 'OPEN', 'CURRENT', 'IN_PROGRESS', 'VIEWED', 'SENT'].includes(status)) return 'warning'
    if (['PARTIALLY_REFUNDED', 'SELECTED', 'SUBMITTED', 'SCHEDULED', 'INVITED'].includes(status)) return 'info'
    return 'neutral'
  })()
  return <span className={`status-badge status-${tone}`}>{children || STATUS_LABELS[status] || status}</span>
}

export function PageTitle({ eyebrow, title, description, actions }) {
  return (
    <div className="page-title-row">
      <div>
        {eyebrow && <div className="page-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-title-actions">{actions}</div>}
    </div>
  )
}

export function Card({ title, description, action, children, className = '' }) {
  return (
    <section className={`card ${className}`}>
      {(title || action) && (
        <header className="card-header">
          <div>
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
          </div>
          {action && <div>{action}</div>}
        </header>
      )}
      <div className="card-body">{children}</div>
    </section>
  )
}

export function MetricCard({ label, value, delta, tone = 'orange' }) {
  return (
    <div className={`metric-card metric-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{delta}</small>
    </div>
  )
}

export function SearchField({ value, onChange, placeholder = '검색어를 입력하세요' }) {
  return (
    <label className="search-field">
      <span aria-hidden="true">⌕</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
    </label>
  )
}

export function EmptyState({ title = '표시할 데이터가 없습니다.', description = '검색 조건을 변경해보세요.' }) {
  return <div className="empty-state"><div>□</div><strong>{title}</strong><p>{description}</p></div>
}

export function DefinitionList({ items }) {
  return (
    <dl className="definition-list">
      {items.map((item) => (
        <div key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value ?? '-'}</dd>
        </div>
      ))}
    </dl>
  )
}

export function ProgressBar({ value, max = 100, label }) {
  const percentage = Math.max(0, Math.min(100, (Number(value) / max) * 100))
  return (
    <div className="progress-wrap">
      {label && <div className="progress-label"><span>{label}</span><strong>{Number(value).toFixed(value < 1 ? 2 : 0)}{max === 100 ? '%' : ''}</strong></div>}
      <div className="progress-track"><span style={{ width: `${percentage}%` }} /></div>
    </div>
  )
}
